/* eslint-disable react/jsx-filename-extension */
import '@designsforhealth/dfh-fonts-gotham'
import '@designsforhealth/dfh-fonts-gotham-condensed'
import '@designsforhealth/dfh-fonts-gotham-narrow'
import '@designsforhealth/dfh-fonts-gotham-xnarrow'
import '@fontsource/herr-von-muellerhoff'

import { ThemeProvider as MuiThemeProvider } from '@material-ui/styles'
import React from 'react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'

import { GlobalSubscriptions } from './src/components/GlobalSubscriptions'
import Auth0WithFallbackProvider from './src/context/Auth0WithFallbackProvider'
import AuthorizedApolloProvider from './src/context/AuthorizedApolloProvider'
import CartContextProvider from './src/context/CartContextProvider'
import ClickWrapModalContextProvider from './src/context/ClickWrapModalContextProvider'
import CompletedCartsContextProvider from './src/context/CompletedCartsContextProvider'
import HubSpotContextProvider from './src/context/HubSpotContextProvider'
import {
  LocationContextProvider,
  LocationContextUpdate,
} from './src/context/LocationContextProvider'
import SanityUserRolesContextProvider from './src/context/SanityUserRolesContextProvider'
import StyledModalProvider from './src/context/StyledModalProvider'
import ToastContextProvider from './src/context/ToastContextProvider'
import { ToggleCartContextProvider } from './src/context/ToggleCartContextProvider'
import TokenContextProvider from './src/context/TokenContextProvider'
import { getLoginCallbackUrl } from './src/lib/auth'
import { onRedirectCallback } from './src/lib/auth/callback'
import { requestedScopes } from './src/lib/auth/config'
import { getPrivacyControlOptions } from './src/lib/conversions/actions'
import { setConversionsEventMeta } from './src/lib/conversions/state'
import theme from './src/theme'
import customTheme from './src/theme/mui'

import '@fontsource/roboto-condensed/latin.css'
import '@fontsource/roboto/latin.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

// Cypress tests use localStorage for tokens
const auth0CacheLocation =
  process.env.GATSBY_AUTH0_CACHE_LOCATION === 'localStorage' ? 'localstorage' : 'memory'

export const wrapRootElement = ({ element }) => (
  <Auth0WithFallbackProvider
    domain={process.env.GATSBY_AUTH0_DOMAIN}
    clientId={process.env.GATSBY_AUTH0_CLIENTID}
    audience={process.env.GATSBY_AUTH0_AUDIENCE}
    scope={requestedScopes.join(' ')}
    redirectUri={getLoginCallbackUrl()}
    onRedirectCallback={onRedirectCallback}
    cacheLocation={auth0CacheLocation}
  >
    <TokenContextProvider>
      <AuthorizedApolloProvider>
        <LocationContextProvider>
          <ToggleCartContextProvider>
            <CartContextProvider>
              <ToastContextProvider>
                <CompletedCartsContextProvider>
                  <SanityUserRolesContextProvider>
                    <StyledThemeProvider theme={theme}>
                      <MuiThemeProvider theme={customTheme}>
                        <ClickWrapModalContextProvider>
                          <StyledModalProvider>
                            <HubSpotContextProvider>
                              <GlobalSubscriptions />
                              {element}
                            </HubSpotContextProvider>
                          </StyledModalProvider>
                        </ClickWrapModalContextProvider>
                      </MuiThemeProvider>
                    </StyledThemeProvider>
                  </SanityUserRolesContextProvider>
                </CompletedCartsContextProvider>
              </ToastContextProvider>
            </CartContextProvider>
          </ToggleCartContextProvider>
        </LocationContextProvider>
      </AuthorizedApolloProvider>
    </TokenContextProvider>
  </Auth0WithFallbackProvider>
)

export const wrapPageElement = ({ element, props: { location } }) => {
  return <LocationContextUpdate location={location}>{element}</LocationContextUpdate>
}

export const onRouteUpdate = () => {
  // wrap inside a timeout to make sure react-helmet is done with its changes (https://github.com/gatsbyjs/gatsby/issues/11592)
  const sendPageView = () => {
    // push event using RxJS signal
    setConversionsEventMeta({
      event_name: 'PageView',
      ...getPrivacyControlOptions(),
    })
  }

  if (`requestAnimationFrame` in window) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTimeout(sendPageView, 0))
    })
  } else {
    // Delay by 32ms to simulate 2 requestOnAnimationFrame calls
    setTimeout(sendPageView, 32)
  }

  return null
}
